import { useEffect, useState } from 'react'
import { subscribeBroadcast } from '@highlight/ipc'
import { orderLivePartials, type PartialTranscriptions } from '@renderer/hooks/audio/use-partial-transcriptions'
import {
  TRANSCRIPT_PARTIALS_TOPIC,
  type TranscriptPartialMessage,
  type TranscriptPartialSource,
} from '@shared/transcript-feed'

/** Partials stream at the transcriber's rate; the preview renders at most this often. */
const PREVIEW_INTERVAL_MS = 250
/** A line that just finalized stays up this long, so the preview doesn't blink between utterances. */
const PREVIEW_HOLD_MS = 1_500
/** Only the tail of a line fits a one-line preview, so longer text isn't worth laying out. */
const PREVIEW_MAX_CHARS = 160

/** Partials only know which audio stream they came from, not who's speaking. */
export const PARTIAL_SPEAKER_LABELS: Record<TranscriptPartialSource, string> = {
  microphone: 'You',
  loopback: 'Them',
}

export interface TranscriptPreview {
  source: TranscriptPartialSource
  text: string
}

function pickLivePreview(partials: PartialTranscriptions): TranscriptPreview | null {
  const live = orderLivePartials(partials).at(-1)
  return live ? { source: live.source, text: live.text.slice(-PREVIEW_MAX_CHARS) } : null
}

function isSamePreview(a: TranscriptPreview | null, b: TranscriptPreview | null): boolean {
  return a?.source === b?.source && a?.text === b?.text
}

/**
 * Calls `onPreview` with the live partial line, throttled to {@link PREVIEW_INTERVAL_MS} and only
 * when it changes. Returns the unsubscribe.
 */
export function subscribeTranscriptPreview(onPreview: (preview: TranscriptPreview | null) => void): () => void {
  const partials: PartialTranscriptions = { microphone: null, loopback: null }
  let shown: TranscriptPreview | null = null
  let flushTimer: ReturnType<typeof setTimeout> | undefined
  let clearTimer: ReturnType<typeof setTimeout> | undefined

  const show = (preview: TranscriptPreview | null) => {
    shown = preview
    onPreview(preview)
  }

  const flush = () => {
    flushTimer = undefined
    const next = pickLivePreview(partials)
    if (!next) {
      if (shown && !clearTimer) {
        clearTimer = setTimeout(() => {
          clearTimer = undefined
          show(null)
        }, PREVIEW_HOLD_MS)
      }
      return
    }
    clearTimeout(clearTimer)
    clearTimer = undefined
    if (!isSamePreview(shown, next)) {
      show(next)
    }
  }

  const unsubscribe = subscribeBroadcast<TranscriptPartialMessage>(TRANSCRIPT_PARTIALS_TOPIC, (message) => {
    partials[message.source] = message.partial
    flushTimer ??= setTimeout(flush, PREVIEW_INTERVAL_MS)
  })

  return () => {
    unsubscribe()
    clearTimeout(flushTimer)
    clearTimeout(clearTimer)
  }
}

/** The live partial line, subscribed only while mounted. */
export function useLiveTranscriptPreview(): TranscriptPreview | null {
  const [preview, setPreview] = useState<TranscriptPreview | null>(null)
  useEffect(() => subscribeTranscriptPreview(setPreview), [])
  return preview
}
